/**
 * PLAN-OFFER-01 slices C / D / E (2026-10-29, 34i102): the waiting plans.
 *
 * A plan offer is a proposal of type Plan — the same `pegasus_plan_sale` row the
 * homeowner's checkbox, the office's Plan Builder and the sweep all write. One
 * list (`v_plan_offer`) feeds the 360 Proposals card and Memberships → Offers,
 * so nothing can be in one place and not the other (Seth: "I don't want it to
 * get too confusing or miss something").
 *
 * States, everywhere the same: Quoted (the office priced it in the Plan Builder,
 * nothing sent) · Offered · Viewed · Accepted · Unpaid (the one that must never
 * hide — it names the invoice it waits on) · Active · Declined · Expired.
 * Resend keeps the price it was sent with; payment is the activation.
 */
import { type PlanOfferCover } from "@/lib/planCovers";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface PlanOfferRow {
  sale_id: string;
  custno: string;
  locno: string | null;
  customer_name: string | null;
  address: string | null;
  city: string | null;
  status: string;
  term: number;
  base_id: string | null;
  base_name: string | null;
  plan_name: string;
  total: number | null;
  price_snapshot: Record<string, number> | null;
  offer_quote_id: string | null;
  quote_title: string | null;
  quote_type: string | null;
  quote_token: string | null;
  quote_status: string | null;
  quote_dispatch_no: string | null;
  quote_sent_at: string | null;
  quote_viewed_at: string | null;
  quote_last_viewed_at: string | null;
  quote_view_count: number;
  quote_accepted: boolean | null;
  offered_on: "repair" | "install" | "office" | "plan_only";
  offer_mode: string | null;
  offered_at: string | null;
  offer_expires_at: string | null;
  accepted_at: string | null;
  accepted_via: string | null;
  declined_at: string | null;
  declined_reason: string | null;
  paid_at: string | null;
  activated_at: string | null;
  sold_at: string | null;
  start_date: string | null;
  expire_date: string | null;
  offer_invoice_id: string | null;
  invoice_no: string | null;
  invoice_total: number | null;
  invoice_balance: number | null;
  esc_invoice_no: string | null;
  credit_amount: number | null;
  credit_invoice: string | null;
  first_check_season: string | null;
  first_check_year: number | null;
  /** PROPOSAL-EQUIPMENT-02: the systems the offer covers (count + which units). */
  systems?: number | null;
  covers?: PlanOfferCover[] | null;
  created_at: string;
  sold_by: string | null;
}

export type OfferState = "quoted" | "offered" | "viewed" | "accepted_unpaid" | "active" | "declined" | "expired" | "other";

export function offerState(o: Pick<PlanOfferRow, "status" | "quote_viewed_at" | "quote_view_count" | "offer_expires_at">): OfferState {
  switch (o.status) {
    case "quote": return "quoted";
    case "offered":
      if (o.offer_expires_at && new Date(o.offer_expires_at).getTime() < Date.now()) return "expired"; // reads expired before the sweep flips it
      return o.quote_viewed_at || (o.quote_view_count ?? 0) > 0 ? "viewed" : "offered";
    case "accepted": return "accepted_unpaid";
    case "sold": return "active";
    case "declined": return "declined";
    case "expired": return "expired";
    default: return "other"; // cancelled · removed
  }
}

/** Chip text + colour per state — the 360 card and the Offers table share it. */
export const OFFER_STATE: Record<OfferState, { label: string; chip: string }> = {
  quoted: { label: "Quoted", chip: "bg-slate-100 text-slate-600" },
  offered: { label: "Offered", chip: "bg-sky-50 text-sky-700" },
  viewed: { label: "Viewed", chip: "bg-violet-50 text-violet-700" },
  accepted_unpaid: { label: "Accepted · Unpaid", chip: "bg-amber-50 text-amber-800" },
  active: { label: "Active", chip: "bg-emerald-50 text-emerald-700" },
  declined: { label: "Declined", chip: "bg-rose-50 text-rose-700" },
  expired: { label: "Expired", chip: "bg-amber-50 text-amber-800" },
  other: { label: "Closed", chip: "bg-slate-100 text-slate-500" },
};

/** "Repair Quote 2281" · "System Proposal · Best" · "Plan only" · "Office quote". */
export function offeredOnLabel(o: Pick<PlanOfferRow, "offered_on" | "quote_dispatch_no" | "quote_title">): string {
  switch (o.offered_on) {
    case "repair": return o.quote_dispatch_no ? `Repair Quote · call #${o.quote_dispatch_no}` : "Repair Quote";
    case "install": return "System Proposal";
    case "office": return "Priced by the office";
    default: return "Plan only";
  }
}

/** Whole days until the offer lapses (negative = past). */
export function offerDaysLeft(o: Pick<PlanOfferRow, "offer_expires_at">): number | null {
  if (!o.offer_expires_at) return null;
  return Math.ceil((new Date(o.offer_expires_at).getTime() - Date.now()) / 864e5);
}

export function daysSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 864e5));
}

export const offerMoney = (v: number | null | undefined) =>
  (Number(v) || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: Number.isInteger(Number(v)) ? 0 : 2 });

export const fmtDay = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—";

/** Short label for the term: "3 yr". */
export const termLabel = (t: number) => `${t} yr`;

const SELECT = "*";

/** Company-wide: every plan offer (Memberships → Offers). */
export function usePlanOffers() {
  return useQuery({
    queryKey: ["plan_offers", "all"],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<PlanOfferRow[]> => {
      const { data, error } = await supabase
        .from("v_plan_offer")
        .select(SELECT)
        .order("offered_at", { ascending: false, nullsFirst: false })
        .limit(1000);
      if (error) throw error;
      return (data ?? []) as PlanOfferRow[];
    },
  });
}

/** This customer's plan offers (the 360 Proposals card). */
export function useCustomerPlanOffers(custno: string | null | undefined) {
  return useQuery({
    queryKey: ["plan_offers", custno],
    enabled: !!custno,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<PlanOfferRow[]> => {
      const { data, error } = await supabase
        .from("v_plan_offer")
        .select(SELECT)
        .eq("custno", custno as string)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as PlanOfferRow[];
    },
  });
}

function invalidateOffers(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["plan_offers"] });
  qc.invalidateQueries({ queryKey: ["customer_proposals"] });
  qc.invalidateQueries({ queryKey: ["followups"] });
  qc.invalidateQueries({ queryKey: ["followups_due"] });
  qc.invalidateQueries({ queryKey: ["quote"] });
  qc.invalidateQueries({ queryKey: ["quotes"] });
  qc.invalidateQueries({ queryKey: ["plan_sales"] });
  qc.invalidateQueries({ queryKey: ["membership_status"] }); qc.invalidateQueries({ queryKey: ["quote_membership_status"] });
  qc.invalidateQueries({ queryKey: ["membership_card"] });
}

export interface ResendResult { ok: boolean; why?: string; quote_id: string | null; token: string | null; expires_at: string; days: number }

/** Resend: same price, a new 60 days; an expired quote re-opens; the follow-up closes.
 *  Nothing is emailed here — the caller opens the proposal page's staff bar to send. */
export function useResendPlanOffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (saleId: string): Promise<ResendResult> => {
      const { data, error } = await supabase.rpc("pegasus_plan_offer_resend", { p_sale_id: saleId });
      if (error) throw error;
      const r = data as ResendResult;
      if (!r?.ok) throw new Error(r?.why || "Couldn't resend");
      return r;
    },
    onSuccess: () => invalidateOffers(qc),
  });
}

export const DECLINE_REASONS = ["Too expensive", "Not interested right now", "Went with someone else", "Selling the house", "Other"] as const;

export function useDeclinePlanOffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { saleId: string; reason: string }) => {
      const { error } = await supabase.rpc("pegasus_plan_offer_decline_sale", { p_sale_id: v.saleId, p_reason: v.reason });
      if (error) throw error;
    },
    onSuccess: () => invalidateOffers(qc),
  });
}

/** The proposal page URL for an offer that rides a quote (the staff bar sends from there). */
export function offerProposalUrl(o: Pick<PlanOfferRow, "quote_token">): string | null {
  if (!o.quote_token) return null;
  const base = typeof window !== "undefined" && /localhost|127\.0\.0\.1/.test(window.location.hostname)
    ? window.location.origin
    : "https://my.sedgwickheating.com";
  return `${base}/proposal?t=${encodeURIComponent(o.quote_token)}`;
}
